/**
 * WHAT A SET COSTS THE GUEST, given what they picked (CAT-018, founder decision
 * 2026-09-01).
 *
 * A formule has ONE price on its card, and an option of a `CHOICE` section may
 * carry a SUPPLEMENT: what the guest pays on top for picking that one, per unit
 * of the pick, VAT included. The set's price is therefore a RANGE - the card's
 * price at the cheapest picks, the card's price plus every section's dearest
 * supplement at the dearest - and one basket line is the card's price plus the
 * supplements of exactly what was picked.
 *
 * Stated ONCE, over the shapes every surface already has: the guest's tile,
 * sheet and basket read the card and the answers; the worker prices a line off
 * the components it resolved; the Internal menu prints the range. Each of
 * those did its own `price x quantity` before a supplement existed, and a
 * supplement added at three of the four is the fourth screen disagreeing with
 * the checkout by exactly one dear juice.
 *
 * An included item carries no supplement (there is nothing to pick), and the
 * server stores 0 under an `INCLUDED` section whatever it was sent - so the
 * arithmetic below reads the field wherever it stands and never asks the kind
 * before adding.
 */

/** Anything with a count and an optional per-unit supplement, in minor units. */
export interface SupplementedPart {
  readonly quantity: number;
  /** Absent means none: a component placed before supplements existed. */
  readonly supplement?: number | undefined;
}

/**
 * A section of a card as every surface has it: the guest's `FormuleBullet`,
 * the editor's `CatalogueComponent`, the composition record. A `CHOICE`
 * section counts the PICK (`quantity`), an `INCLUDED` section counts each item.
 */
export interface SupplementedBullet {
  readonly rank: number;
  readonly kind: 'INCLUDED' | 'CHOICE';
  readonly quantity: number;
  readonly choices: readonly (SupplementedPart & { readonly itemId: string })[];
}

export interface MinorRangeView {
  readonly min: number;
  readonly max: number;
}

/** Does this card ask the guest anything at all? False for a card of included items only. */
export function formuleAsks(card: readonly { readonly kind: string }[] | undefined): boolean {
  return card?.some((bullet) => bullet.kind === 'CHOICE') ?? false;
}

/**
 * The supplements of a set's resolved parts, summed: what a placed line's
 * components add to the card's price, per unit of the set. Each part carries
 * its own count - the section's count for a picked option, its own for an
 * included item - so this is `sum(quantity x supplement)` and nothing else.
 */
export function partsSupplementMinor(parts: readonly SupplementedPart[]): number {
  let total = 0;
  for (const part of parts) total += part.quantity * (part.supplement ?? 0);
  return total;
}

/**
 * The cheapest and dearest supplement a card allows, over the sections: a
 * `CHOICE` section contributes its count times its cheapest / dearest option,
 * an `INCLUDED` section contributes every item at its own count (0 by the
 * server's rule, read anyway). A section with nothing in it contributes 0.
 */
export function formuleSupplementRange(card: readonly SupplementedBullet[]): MinorRangeView {
  let min = 0;
  let max = 0;
  for (const bullet of card) {
    if (bullet.kind === 'INCLUDED') {
      const included = partsSupplementMinor(bullet.choices);
      min += included;
      max += included;
      continue;
    }
    if (bullet.choices.length === 0) continue;
    const supplements = bullet.choices.map((choice) => choice.supplement ?? 0);
    min += bullet.quantity * Math.min(...supplements);
    max += bullet.quantity * Math.max(...supplements);
  }
  return { min, max };
}

/** `price` at the cheapest picks to `price` plus every dearest pick. */
export function formuleGuestPriceRange(
  price: number,
  card: readonly SupplementedBullet[],
): MinorRangeView {
  const range = formuleSupplementRange(card);
  return { min: price + range.min, max: price + range.max };
}

/**
 * One unit of a set as the guest answered it: the card's price plus, for every
 * `CHOICE` section, the picked option's supplement times the section's count.
 * An unanswered section adds nothing - the sheet prices what is chosen so far
 * and the checkout refuses the line, which is not this function's question.
 */
export function formuleUnitPriceMinor(
  price: number,
  card: readonly SupplementedBullet[],
  answers: readonly { readonly componentRank: number; readonly itemId: string }[],
): number {
  let total = price;
  for (const bullet of card) {
    if (bullet.kind === 'INCLUDED') {
      total += partsSupplementMinor(bullet.choices);
      continue;
    }
    const answer = answers.find((candidate) => candidate.componentRank === bullet.rank);
    if (answer === undefined) continue;
    const picked = bullet.choices.find((choice) => choice.itemId === answer.itemId);
    if (picked === undefined) continue;
    total += bullet.quantity * (picked.supplement ?? 0);
  }
  return total;
}
